import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMarketNavigate } from '../hooks/useMarketNavigate';
import { getRestaurants, getHappyHourDeals, getRestaurantSpecials, getEvents, getSettings, getAdvertisements, clearAirtableCache, getCustomKeywordsForBase } from '../services/airtable';
import { clearSession } from '../utils/auth';
import SEOHead from '../components/SEOHead';
import AutoScrollCarousel from '../components/AutoScrollCarousel';
import AdBanner from '../components/AdBanner';
import FilterPanel, { resolveDayFilter, resolveWhenDateRange, getFilterLabel } from '../components/FilterPanel';
import HamburgerMenu from '../components/HamburgerMenu';
import { useMarket } from '../context/MarketContext';
import { getSwitcherMarkets } from '../config/markets';
import {
  FOOD_KEYWORDS,
  DRINK_KEYWORDS,
  countKeywords,
  categorizeDeal,
  categorizeItem,
  parseItemsFromDescription,
  groupDealsByCategory,
  formatDescription,
  extractPrice,
  formatDaysLabel,
  setCustomKeywords,
} from '../utils/descriptionParser';
import { trackVenueExpanded, trackSearch, trackMarketSwitched, trackAddToCalendar, trackFilterApplied, trackEvent as gaEvent } from '../utils/analytics';
import { ContentItem, ContentItemSection, renderMarkdown } from '../components/ContentItem';
import '../App.css';

/**
 * Parse an Event Start value safely, handling both date-only ("YYYY-MM-DD")
 * and datetime ("YYYY-MM-DDT19:00:00-06:00") strings.
 *
 * Date-only values are parsed with T12:00:00 to avoid UTC midnight causing
 * the date to display as the previous day in US timezones.
 */
function parseEventDate(dateStr) {
  if (!dateStr) return null;
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  const d = new Date(isDateOnly ? dateStr + 'T12:00:00' : dateStr);
  return isNaN(d.getTime()) ? null : d;
}

function HomePage() {
  const rawNavigate = useNavigate();
  const navigate = useMarketNavigate();
  const market = useMarket();

  const handleLogout = () => {
    clearSession();
    navigate('/login');
  };
  const [restaurants, setRestaurants] = useState([]);
  const [deals, setDeals] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Modern filter system - use market's default cities
  const [filters, setFilters] = useState({ when: [], what: [], where: market.defaultCities, happeningNow: false });
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [expandedVenues, setExpandedVenues] = useState({});
  const [eventPages, setEventPages] = useState({}); // Per-venue event pagination (pages of 4)
  const [calendarModal, setCalendarModal] = useState(null);
  const [version, setVersion] = useState(null);
  const [settings, setSettings] = useState([]);
  const [ads, setAds] = useState([]);

  // Search/autocomplete state
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef(null);
  const filterBarRef = useRef(null);
  const regionBarRef = useRef(null);
  const [marketDropdownOpen, setMarketDropdownOpen] = useState(false);
  const [showFloatingClear, setShowFloatingClear] = useState(false);

  // Pull-to-refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const touchStartY = useRef(0);
  const containerRef = useRef(null);
  const PULL_THRESHOLD = 80;

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (!isRefresh) setLoading(true);
      const [restaurantsData, dealsData, specialsData, eventsData, settingsData, adsData, customKw] = await Promise.all([
        getRestaurants(),
        getHappyHourDeals(),
        getRestaurantSpecials(),
        getEvents(),
        getSettings(),
        getAdvertisements(),
        getCustomKeywordsForBase(market.airtableBaseId),
      ]);

      setCustomKeywords(customKw.food, customKw.drink);
      setRestaurants(restaurantsData);
      setDeals(dealsData);
      setSpecials(specialsData);
      setEvents(eventsData);
      setSettings(settingsData);
      setAds(adsData);
      setLoading(false);
    } catch (err) {
      setError('Failed to load data');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market.id]);

  useEffect(() => {
    fetchData();

    // Fetch version info
    fetch('/version.json')
      .then(res => res.json())
      .then(data => setVersion(data))
      .catch(() => setVersion({ commit: 'dev', buildTime: new Date().toISOString() }));
  }, [fetchData]);

  // Reset filters and UI state when market changes (e.g. switching via region dropdown)
  useEffect(() => {
    setFilters({ when: [], what: [], where: market.defaultCities, happeningNow: false });
    setExpandedVenues({});
    setEventPages({});
    setMarketDropdownOpen(false);
  }, [market.id, market.defaultCities]);

  // Pull-to-refresh handlers
  const handleTouchStart = (e) => {
    if (containerRef.current?.scrollTop === 0) {
      touchStartY.current = e.touches[0].clientY;
    }
  };

  const handleTouchMove = (e) => {
    if (touchStartY.current === 0 || isRefreshing) return;

    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStartY.current;

    if (diff > 0 && containerRef.current?.scrollTop === 0) {
      setPullDistance(Math.min(diff * 0.5, PULL_THRESHOLD * 1.5));
    }
  };

  const handleTouchEnd = async () => {
    if (pullDistance >= PULL_THRESHOLD && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(PULL_THRESHOLD);
      clearAirtableCache(); // Force fresh data on pull-to-refresh
      await fetchData(true);
      setIsRefreshing(false);
    }
    setPullDistance(0);
    touchStartY.current = 0;
  };

  const toggleVenueSection = (venueId, section) => {
    const isOpening = !expandedVenues[venueId]?.[section];
    if (isOpening) {
      const venue = restaurants.find(r => r.id === venueId);
      if (venue) trackVenueExpanded(venue.fields?.Name || venueId, market.id);
    }
    setExpandedVenues(prev => ({
      ...prev,
      [venueId]: {
        ...prev[venueId],
        [section]: !prev[venueId]?.[section]
      }
    }));
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';

    // Already in 12-hour format with AM/PM
    if (timeString.match(/^\d{1,2}:\d{2}\s*(AM|PM)$/i)) {
      return timeString;
    }

    // Handle 24-hour format like "16:00" or "19:00" from Airtable
    const time24Match = timeString.match(/^(\d{1,2}):(\d{2})$/);
    if (time24Match) {
      let hours = parseInt(time24Match[1], 10);
      const minutes = time24Match[2];
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${hours}:${minutes} ${ampm}`;
    }

    try {
      const date = new Date(timeString);
      if (isNaN(date.getTime())) {
        return timeString;
      }
      let hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;
      const minutesStr = minutes < 10 ? '0' + minutes : minutes;
      return `${hours}:${minutesStr} ${ampm}`;
    } catch (e) {
      return timeString;
    }
  };

  const formatTimeRange = (startTime, endTime) => {
    const start = formatTime(startTime);
    const end = formatTime(endTime);

    if (!start || !end) return '';

    // Detect "all day" patterns (12:00 AM - 11:59 PM, 12:00 AM - 12:00 AM, etc.)
    const startNorm = start.replace(/\s+/g, ' ').trim().toUpperCase();
    const endNorm = end.replace(/\s+/g, ' ').trim().toUpperCase();
    if ((startNorm === '12:00 AM' || startNorm === '12:00AM') &&
        (endNorm === '11:59 PM' || endNorm === '11:59PM' || endNorm === '12:00 AM' || endNorm === '12:00AM')) {
      return 'All Day';
    }

    const startPeriod = start.match(/(AM|PM)/i)?.[0];
    const endPeriod = end.match(/(AM|PM)/i)?.[0];

    if (startPeriod === endPeriod) {
      const startWithoutPeriod = start.replace(/\s*(AM|PM)/i, '');
      return `${startWithoutPeriod} - ${end}`;
    }

    return `${start} - ${end}`;
  };

  // Helper function to convert ALL CAPS text to Title Case
  const formatTextCase = (text) => {
    if (!text) return '';
    // Check if text is mostly uppercase (more than 50% caps)
    const upperCount = (text.match(/[A-Z]/g) || []).length;
    const letterCount = (text.match(/[a-zA-Z]/g) || []).length;
    if (letterCount > 0 && upperCount / letterCount > 0.5) {
      // Convert to title case
      return text
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return text;
  };

  // Helper function to get category fallback image based on event type
  // TCC uses unprefixed names; all OTC markets share the same 'otc-' prefixed images
  const getEventCategoryImage = (eventType) => {
    const prefix = market.id === 'tcc' ? '' : 'otc-';
    const categoryImages = {
      'Live Music': `/images/${prefix}event-live-music.png`,
      'Trivia': `/images/${prefix}event-trivia.png`,
      'Karaoke': `/images/${prefix}event-karaoke.png`,
      'Comedy': `/images/${prefix}event-comedy.png`,
      'Open Mic': `/images/${prefix}event-open-mic.png`,
      'Sports': `/images/${prefix}event-sports.png`,
      'Special Event': `/images/${prefix}event-special.png`,
      'Holiday': `/images/${prefix}event-holiday.png`,
      'DJ': `/images/${prefix}event-dj.png`,
      'Bingo': `/images/${prefix}event-bingo.png`,
      'Other': `/images/${prefix}event-special.png`
    };
    // Exact match first
    if (categoryImages[eventType]) return categoryImages[eventType];
    // Partial match: check if event type contains a known category keyword
    if (eventType) {
      const lower = eventType.toLowerCase();
      for (const [key, image] of Object.entries(categoryImages)) {
        if (key !== 'Other' && lower.includes(key.toLowerCase())) return image;
      }
    }
    return `/images/${prefix}event-special.png`;
  };

  // ============================================================================
  // DEAL CATEGORIZATION & PARSING
  // Imported from lib/descriptionParser.js — see import at top of file
  // ============================================================================

  // Helper function to check if all deals share the same schedule (time + days)
  const getSharedSchedule = (dealsList) => {
    if (!dealsList || dealsList.length === 0) return null;

    const firstDeal = dealsList[0];
    const firstTime = formatTimeRange(firstDeal.fields['Start Time'], firstDeal.fields['End Time']);
    const firstDays = (firstDeal.fields['Days of Week'] || []).sort().join(',');

    const allSame = dealsList.every(deal => {
      const time = formatTimeRange(deal.fields['Start Time'], deal.fields['End Time']);
      const days = (deal.fields['Days of Week'] || []).sort().join(',');
      return time === firstTime && days === firstDays;
    });

    if (allSame) {
      return {
        time: firstTime,
        days: firstDeal.fields['Days of Week'] || [],
        daysLabel: formatDaysLabel(firstDeal.fields['Days of Week'])
      };
    }

    return null;
  };

  // Group deals by their schedule (time + days), then categorize within each group
  // This prevents repeating the same time/days on every individual deal
  const groupDealsBySchedule = (dealsList) => {
    const scheduleMap = new Map();

    dealsList.forEach(deal => {
      const time = formatTimeRange(deal.fields['Start Time'], deal.fields['End Time']);
      const days = (deal.fields['Days of Week'] || []).sort().join(',');
      const recurrenceDesc = deal.fields['Recurrence Description'] || '';
      const key = `${time}|||${days}|||${recurrenceDesc}`;

      if (!scheduleMap.has(key)) {
        scheduleMap.set(key, {
          time,
          days: deal.fields['Days of Week'] || [],
          daysLabel: recurrenceDesc || formatDaysLabel(deal.fields['Days of Week']),
          deals: []
        });
      }
      scheduleMap.get(key).deals.push(deal);
    });

    // Sort groups by earliest day of week, then by number of days (more days first), then by time
    const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    return Array.from(scheduleMap.values()).sort((a, b) => {
      // First: earliest day of week
      const aFirst = a.days.length > 0 ? Math.min(...a.days.map(d => dayOrder.indexOf(d))) : 99;
      const bFirst = b.days.length > 0 ? Math.min(...b.days.map(d => dayOrder.indexOf(d))) : 99;
      if (aFirst !== bFirst) return aFirst - bFirst;
      // Then: more days first (e.g., M-F before just M)
      if (a.days.length !== b.days.length) return b.days.length - a.days.length;
      // Then: more deals first
      return b.deals.length - a.deals.length;
    });
  };

  // Helper function to group specials by their days of week
  const groupSpecialsByDays = (specialsList) => {
    const groups = {};
    const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    specialsList.forEach(special => {
      const days = special.fields['Days of Week'] || [];
      const recurrenceDesc = special.fields['Recurrence Description'] || '';
      const daysKey = recurrenceDesc || ([...days].sort().join(',') || 'no-days');

      if (!groups[daysKey]) {
        groups[daysKey] = {
          days: days,
          label: recurrenceDesc || formatDaysLabel(days),
          specials: [],
          // For sorting: use first day's index, or 99 for no days
          sortIndex: days.length > 0 ? Math.min(...days.map(d => dayOrder.indexOf(d))) : 99
        };
      }
      groups[daysKey].specials.push(special);
    });

    // Sort groups: Daily first, then Weekdays, then Weekends, then by day order
    return Object.values(groups).sort((a, b) => {
      const labelOrder = { 'Daily': -3, 'Weekdays': -2, 'Weekends': -1 };
      const orderA = labelOrder[a.label] ?? a.sortIndex;
      const orderB = labelOrder[b.label] ?? b.sortIndex;
      return orderA - orderB;
    });
  };

  const formatEventDate = (event) => {
    // Handle recurring events
    if (event.fields?.Recurring) {
      const startTime = event.fields['Start Time'];
      const formattedTime = startTime ? formatTime(startTime) : null;

      // Prefer recurrence description for complex patterns (e.g., "1st Monday of every month")
      const recurrenceDesc = event.fields['Recurrence Description'];
      if (recurrenceDesc) {
        return formattedTime ? `${recurrenceDesc} @ ${formattedTime}` : recurrenceDesc;
      }

      const daysOfWeek = event.fields['Days of Week'] || event.fields['Days of Week '] || [];
      if (daysOfWeek.length > 0) {
        const dayStr = Array.isArray(daysOfWeek) ? daysOfWeek.join(', ') : daysOfWeek;
        // Add 's' to make it plural (e.g., "Wednesday" -> "Wednesdays")
        // Day names don't follow the 'y -> ies' rule - they just add 's'
        const pluralDay = dayStr + 's';
        return formattedTime ? `${pluralDay} @ ${formattedTime}` : pluralDay;
      }
      return 'Recurring';
    }

    // Handle one-time events
    const dateString = event.fields?.['Event Start'] || event;
    if (!dateString) return 'Date TBD';

    // Date-only values ("YYYY-MM-DD") — show date without time
    // Parse with T12:00 to avoid UTC midnight → previous day issues
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
    const date = new Date(isDateOnly ? dateString + 'T12:00:00' : dateString);
    if (isNaN(date.getTime())) return 'Date TBD';

    if (isDateOnly) {
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      });
    }

    // Has time component — include it in display
    const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0;
    if (hasTime) {
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    }
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCalendarLinks = (event, restaurantName, restaurantAddress) => {
    const startDate = parseEventDate(event.fields['Event Start']);
    const endDate = event.fields['Event End']
      ? new Date(event.fields['Event End'])
      : new Date(startDate.getTime() + 2 * 60 * 60 * 1000); // Default 2 hours

    const formatGoogleDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const title = `${event.fields['Event Name']} at ${restaurantName}`;
    const description = `${event.fields.Description || ''}${event.fields['Event Type'] ? '\n\nType: ' + event.fields['Event Type'] : ''}`;
    const location = `${restaurantName}${restaurantAddress ? ', ' + restaurantAddress : ''}`;

    // Google Calendar URL
    const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}&details=${encodeURIComponent(description)}&location=${encodeURIComponent(location)}`;

    // Outlook Calendar URL
    const outlookUrl = `https://outlook.live.com/calendar/0/action/compose?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(description)}&location=${encodeURIComponent(location)}&startdt=${startDate.toISOString()}&enddt=${endDate.toISOString()}`;

    return {
      google: googleUrl,
      outlook: outlookUrl
    };
  };

  const openCalendarModal = (event, restaurantName, restaurantAddress) => {
    trackAddToCalendar(event.fields?.['Event Name'] || 'Unknown', restaurantName);
    setCalendarModal({
      event,
      restaurantName,
      restaurantAddress,
      links: getCalendarLinks(event, restaurantName, restaurantAddress)
    });
  };

  const closeCalendarModal = () => {
    setCalendarModal(null);
  };

  const getDayOfWeek = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[new Date().getDay()];
  };

  const getCurrentTime = () => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  };

  const parseTime = (timeStr) => {
    if (!timeStr) return null;
    
    try {
      const date = new Date(timeStr);
      if (!isNaN(date.getTime())) {
        return date.getHours() * 60 + date.getMinutes();
      }
    } catch (e) {}
    
    const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return null;
    let hours = parseInt(match[1]);
    const minutes = parseInt(match[2]);
    const period = match[3].toUpperCase();
    
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    
    return hours * 60 + minutes;
  };

  // Check if a date matches a complex recurrence pattern (e.g., "1st and 3rd Thursday")
  // Returns true if no ordinal pattern is detected (safe default — show rather than hide)
  const matchesRecurrenceDate = (recurrenceDesc, date) => {
    if (!recurrenceDesc) return true;

    const ordinalMap = { '1st': 1, '2nd': 2, '3rd': 3, '4th': 4, '5th': 5,
                         'first': 1, 'second': 2, 'third': 3, 'fourth': 4, 'fifth': 5 };
    const pattern = /\b(1st|2nd|3rd|4th|5th|first|second|third|fourth|fifth|last)\b/gi;
    const matches = [...recurrenceDesc.matchAll(pattern)];

    if (matches.length === 0) return true; // No ordinal pattern, safe default

    const targetDate = date || new Date();
    const dayOfMonth = targetDate.getDate();
    const occurrence = Math.ceil(dayOfMonth / 7);

    const allowedOccurrences = matches.map(m => {
      const word = m[1].toLowerCase();
      if (word === 'last') {
        const nextWeek = new Date(targetDate);
        nextWeek.setDate(nextWeek.getDate() + 7);
        return nextWeek.getMonth() !== targetDate.getMonth() ? occurrence : -1;
      }
      return ordinalMap[word];
    });

    return allowedOccurrences.includes(occurrence);
  };

  // Resolve a WHEN filter dayId to the actual target date for recurrence checking
  const resolveFilterDate = (dayId) => {
    const now = new Date();
    if (dayId === 'today') return now;
    if (dayId === 'tomorrow') {
      const tmrw = new Date(now);
      tmrw.setDate(tmrw.getDate() + 1);
      return tmrw;
    }
    if (dayId === 'this_weekend') return null; // Multiple dates — handled separately
    // Specific day name: next occurrence
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const targetDay = dayNames.indexOf(dayId);
    if (targetDay >= 0) {
      const next = new Date(now);
      next.setHours(0, 0, 0, 0);
      const daysUntil = (targetDay - now.getDay() + 7) % 7;
      next.setDate(next.getDate() + (daysUntil === 0 ? 0 : daysUntil));
      return next;
    }
    return now;
  };

  // Check if any resolved filter date matches the recurrence pattern
  const matchesRecurrenceForFilters = (recurrenceDesc, whenFilters) => {
    if (!recurrenceDesc) return true;
    if (whenFilters.length === 0) return true;

    for (const dayId of whenFilters) {
      if (dayId === 'this_weekend') {
        // Check Fri, Sat, Sun of this/next weekend
        const now = new Date();
        const dayOfWeek = now.getDay();
        const friday = new Date(now);
        friday.setHours(0, 0, 0, 0);
        if (dayOfWeek === 0) {
          friday.setDate(friday.getDate() - 2);
        } else {
          const daysUntilFriday = (5 - dayOfWeek + 7) % 7;
          friday.setDate(friday.getDate() + daysUntilFriday);
        }
        for (let i = 0; i < 3; i++) {
          const d = new Date(friday);
          d.setDate(d.getDate() + i);
          if (matchesRecurrenceDate(recurrenceDesc, d)) return true;
        }
      } else {
        const targetDate = resolveFilterDate(dayId);
        if (targetDate && matchesRecurrenceDate(recurrenceDesc, targetDate)) return true;
      }
    }
    return false;
  };

  const isHappeningNow = (deal) => {
    const today = getDayOfWeek();
    const now = getCurrentTime();

    if (!deal.fields['Days of Week']?.includes(today)) return false;
    if (!matchesRecurrenceDate(deal.fields['Recurrence Description'], new Date())) return false;
    
    const startTime = parseTime(deal.fields['Start Time']);
    const endTime = parseTime(deal.fields['End Time']);
    
    if (startTime === null || endTime === null) return false;
    
    return now >= startTime && now <= endTime;
  };

  // Check if an event is happening right now
  const isEventHappeningNow = (event) => {
    const now = new Date();
    const today = getDayOfWeek();
    const currentTime = getCurrentTime();

    if (event.fields.Recurring) {
      // Recurring event: check day of week + recurrence pattern + start time
      if (!event.fields['Days of Week']?.includes(today)) return false;
      if (!matchesRecurrenceDate(event.fields['Recurrence Description'], now)) return false;
      const startTime = parseTime(event.fields['Start Time']);
      if (startTime === null) return false;
      // Consider a recurring event "happening now" if it started within the last 3 hours
      return currentTime >= startTime && currentTime <= startTime + 180;
    } else {
      // One-time event: check Event Start/End dates
      if (!event.fields['Event Start']) return false;
      const eventStart = parseEventDate(event.fields['Event Start']);
      if (!eventStart) return false;

      if (event.fields['Event End']) {
        const eventEnd = new Date(event.fields['Event End']);
        if (!isNaN(eventEnd.getTime())) {
          return now >= eventStart && now <= eventEnd;
        }
      }
      // No end time: consider happening if started within last 3 hours
      const threeHoursLater = new Date(eventStart.getTime() + 3 * 60 * 60 * 1000);
      return now >= eventStart && now <= threeHoursLater;
    }
  };

  const filterDeals = (restaurantDeals) => {
    let filtered = restaurantDeals;

    // Happening Now filter
    if (filters.happeningNow) {
      filtered = filtered.filter(isHappeningNow);
    }

    // WHEN filter - resolve day selections to actual day names + recurrence check
    if (filters.when.length > 0) {
      const resolvedDays = filters.when.flatMap(resolveDayFilter);
      const uniqueDays = [...new Set(resolvedDays)];
      filtered = filtered.filter(deal =>
        deal.fields['Days of Week']?.some(d => uniqueDays.includes(d)) &&
        matchesRecurrenceForFilters(deal.fields['Recurrence Description'], filters.when)
      );
    }

    return filtered;
  };

  const getDealsByRestaurant = (restaurantId) => {
    const restaurantDeals = deals.filter(deal =>
      deal.fields.Restaurant && deal.fields.Restaurant.includes(restaurantId)
    );

    // Helper to normalize text for comparison (remove common prefixes, lowercase, trim)
    const normalizeForComparison = (text) => {
      if (!text) return '';
      return text
        .toLowerCase()
        .replace(/^happy\s*hour\s*/i, '')
        .replace(/\s+specials?$/i, '')
        .replace(/\s+/g, ' ')
        .trim();
    };

    // Helper to check if two descriptions are essentially the same content
    const isSimilarDescription = (desc1, desc2) => {
      if (!desc1 || !desc2) return desc1 === desc2;
      // If one description contains the other (or vice versa), they're similar
      const norm1 = normalizeForComparison(desc1);
      const norm2 = normalizeForComparison(desc2);
      if (norm1 === norm2) return true;
      // Check if one is a subset of the other (for combined vs individual deals)
      if (norm1.includes(norm2) || norm2.includes(norm1)) return true;
      return false;
    };

    // Remove duplicates - improved logic to catch similar deals
    const uniqueDeals = [];
    const seenExact = new Set();
    const seenNormalized = new Map(); // Map of normalized name -> deal

    restaurantDeals.forEach(deal => {
      const name = deal.fields['Deal Name'] || '';
      const desc = deal.fields.Description || '';
      const timeKey = `${deal.fields['Start Time']}|${deal.fields['End Time']}|${(deal.fields['Days of Week'] || []).sort().join(',')}`;

      // Exact duplicate check
      const exactKey = `${name}|${desc}|${timeKey}`;
      if (seenExact.has(exactKey)) return;
      seenExact.add(exactKey);

      // Normalized name check (catch "Happy Hour Appetizers" vs "Appetizer Specials")
      const normalizedName = normalizeForComparison(name);
      const lookupKey = `${normalizedName}|${timeKey}`;

      if (seenNormalized.has(lookupKey)) {
        const existingDeal = seenNormalized.get(lookupKey);
        // If descriptions are similar, skip this one (keep the first)
        if (isSimilarDescription(desc, existingDeal.fields.Description)) {
          return;
        }
      }

      // Check if this deal's content is fully contained in a "combined" deal we already have
      const isCoveredByCombinedDeal = uniqueDeals.some(existing => {
        const existingDesc = existing.fields.Description || '';
        // If existing deal has a much longer description that contains this deal's name
        if (existingDesc.length > desc.length * 1.5 &&
            existingDesc.toLowerCase().includes(normalizedName)) {
          return true;
        }
        return false;
      });

      if (isCoveredByCombinedDeal) return;

      seenNormalized.set(lookupKey, deal);
      uniqueDeals.push(deal);
    });

    return filterDeals(uniqueDeals);
  };

  const getSpecialsByRestaurant = (restaurantId) => {
    const restaurantSpecials = specials.filter(special =>
      special.fields.Restaurant && special.fields.Restaurant.includes(restaurantId)
    );

    // Remove duplicates based on special content
    const seen = new Set();
    const uniqueSpecials = restaurantSpecials.filter(special => {
      const key = [
        special.fields['Special Name'],
        special.fields.Description,
        special.fields['Days of Week']?.join(',')
      ].join('|');

      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    return filterDeals(uniqueSpecials);
  };

  const getEventsByRestaurant = (restaurantId) => {
    const restaurantEvents = events.filter(event =>
      event.fields.Restaurant && event.fields.Restaurant.includes(restaurantId)
    );

    // Remove duplicates based on event content
    const seen = new Set();
    const uniqueEvents = restaurantEvents.filter(event => {
      const key = [
        event.fields['Event Name'],
        event.fields.Description,
        event.fields['Event Start'] || event.fields['Start Time'],
        event.fields['Event Type']
      ].join('|');

      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // Separate recurring and one-time events
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Resolve WHEN filters to actual day names
    const resolvedWhenDays = filters.when.length > 0
      ? [...new Set(filters.when.flatMap(resolveDayFilter))]
      : [];

    // Filter and process events
    const processedEvents = uniqueEvents.filter(event => {
      const isRecurring = event.fields.Recurring;

      if (isRecurring) {
        // Recurring events: check if they match the day filter + recurrence pattern
        const eventDays = event.fields['Days of Week'] || [];
        if (resolvedWhenDays.length > 0) {
          return eventDays.some(d => resolvedWhenDays.includes(d)) &&
            matchesRecurrenceForFilters(event.fields['Recurrence Description'], filters.when);
        }
        return eventDays.length > 0; // Show if it has any days set
      } else {
        // One-time events: filter by date
        if (!event.fields['Event Start']) return false;
        const eventDate = parseEventDate(event.fields['Event Start']);
        if (!eventDate) return false;

        // Only show today and future events
        if (eventDate < today) return false;

        // Apply WHEN filter — OR logic across two independent strategies:
        // - Temporal filters (today/tomorrow/this_weekend): use specific date range
        // - Day-name filters (Mon-Sun): match by day of week across all future occurrences
        if (filters.when.length > 0) {
          const temporalIds = ['today', 'tomorrow', 'this_weekend'];
          const temporalFilters = filters.when.filter(id => temporalIds.includes(id));
          const dayNameFilters = filters.when.filter(id => !temporalIds.includes(id));
          if (temporalFilters.length > 0) {
            const dateRange = resolveWhenDateRange(temporalFilters);
            if (dateRange && eventDate >= dateRange.minDate && eventDate <= dateRange.maxDate) return true;
          }
          if (dayNameFilters.length > 0) {
            const eventDay = days[eventDate.getDay()];
            return dayNameFilters.includes(eventDay);
          }
          return false;
        }
        return true;
      }
    });

    // Apply Happening Now filter (mirrors filterDeals() for deals/specials)
    const finalEvents = filters.happeningNow
      ? processedEvents.filter(isEventHappeningNow)
      : processedEvents;

    // Sort events: recurring events first (by day), then one-time events by date
    return finalEvents.sort((a, b) => {
      const aRecurring = a.fields.Recurring;
      const bRecurring = b.fields.Recurring;

      // Recurring events come first
      if (aRecurring && !bRecurring) return -1;
      if (!aRecurring && bRecurring) return 1;

      // Both recurring: sort by first day of week
      if (aRecurring && bRecurring) {
        const aDays = a.fields['Days of Week'] || [];
        const bDays = b.fields['Days of Week'] || [];
        const aFirstDay = aDays.length > 0 ? days.indexOf(aDays[0]) : 7;
        const bFirstDay = bDays.length > 0 ? days.indexOf(bDays[0]) : 7;
        return aFirstDay - bFirstDay;
      }

      // Both one-time: sort by date
      const dateA = parseEventDate(a.fields['Event Start']);
      const dateB = parseEventDate(b.fields['Event Start']);
      return dateA - dateB;
    });
  };

  // Get future one-time events beyond the current WHEN filter window
  // Only active when WHEN filter is set and no WHAT filter is active
  const getUpcomingEventsByRestaurant = (restaurantId) => {
    if (filters.when.length === 0 || filters.what.length > 0) return [];

    const dateRange = resolveWhenDateRange(filters.when);
    if (!dateRange) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const restaurantEvents = events.filter(event =>
      event.fields.Restaurant && event.fields.Restaurant.includes(restaurantId)
    );

    return restaurantEvents.filter(event => {
      if (event.fields.Recurring) return false;
      if (!event.fields['Event Start']) return false;
      const eventDate = parseEventDate(event.fields['Event Start']);
      if (!eventDate || eventDate < today) return false;
      // Upcoming = after the filter's date range
      return eventDate > dateRange.maxDate;
    }).sort((a, b) => parseEventDate(a.fields['Event Start']) - parseEventDate(b.fields['Event Start']));
  };

  // Check if carousels are enabled via Settings table
  const showEventsCarousel = settings.find(s => s.fields['Setting Name'] === 'show_events_carousel')?.fields.Enabled ?? false;
  const showHappyHourCarousel = settings.find(s => s.fields['Setting Name'] === 'show_happyhour_carousel')?.fields.Enabled ?? false;
  // Carousel auto-scroll settings
  const carouselAutoScroll = settings.find(s => s.fields['Setting Name'] === 'carousel_auto_scroll')?.fields.Enabled ?? true;
  const carouselScrollSpeed = settings.find(s => s.fields['Setting Name'] === 'carousel_scroll_speed')?.fields.Value ?? 4;
  // Spotlight effect: scale at center (1.1 = 10% bigger). Set to 1.0 to disable.
  const carouselScaleMax = settings.find(s => s.fields['Setting Name'] === 'carousel_scale_max')?.fields.Value ?? 1.0;
  // Momentum friction: how long swipe glides (0.90 = quick stop, 0.98 = long glide). Default: 0.92
  const carouselFriction = settings.find(s => s.fields['Setting Name'] === 'carousel_friction')?.fields.Value ?? 0.92;

  // Advertising settings
  // Match settings - use case-insensitive comparison to handle variations
  const adSetting = settings.find(s =>
    s.fields['Setting Name']?.toLowerCase().trim() === 'advertising'
  );
  const advertisingEnabled = adSetting?.fields.Enabled ?? false;
  const adFrequency = settings.find(s =>
    s.fields['Setting Name']?.toLowerCase().trim() === 'ad_frequency'
  )?.fields.Value ?? 5;

  // Get active ads: Active=true, within date range, sorted by Priority (highest first)
  const activeAds = ads
    .filter(ad => {
      if (!ad.fields.Active) return false;
      if (!ad.fields.Ad_Image || ad.fields.Ad_Image.length === 0) return false;
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (ad.fields.Start_Date) {
        const start = new Date(ad.fields.Start_Date);
        if (today < start) return false;
      }
      if (ad.fields.End_Date) {
        const end = new Date(ad.fields.End_Date);
        if (today > end) return false;
      }
      return true;
    })
    .sort((a, b) => (b.fields.Priority || 5) - (a.fields.Priority || 5));

  // Helper to get ad for a given slot position (cycles through active ads)
  const getAdForSlot = (slotIndex) => {
    if (!advertisingEnabled || activeAds.length === 0) return null;
    return activeAds[slotIndex % activeAds.length];
  };

  // Get upcoming events for carousel (next 30 days, sorted chronologically)
  // Max 3 events per venue total, and no same venue appearing 3x consecutively
  const MAX_EVENTS_PER_VENUE = 3;
  const upcomingEvents = (() => {
    const sorted = events
      .filter(e => {
        // Only show events from Active venues
        const venue = restaurants.find(r => r.id === e.fields.Restaurant?.[0]);
        if (!venue || venue.fields.Status !== 'Active') return false;

        const eventDate = parseEventDate(e.fields['Event Start']);
        const now = new Date();
        const thirtyDaysOut = new Date();
        thirtyDaysOut.setDate(thirtyDaysOut.getDate() + 30);
        return eventDate >= now && eventDate <= thirtyDaysOut;
      })
      // Sort chronologically (soonest first)
      .sort((a, b) => parseEventDate(a.fields['Event Start']) - parseEventDate(b.fields['Event Start']));

    // Step 1: Limit to max 3 per venue (keep soonest by date)
    const venueCounts = {};
    const capped = sorted.filter(e => {
      const venueId = e.fields.Restaurant?.[0];
      if (!venueId) return false;
      venueCounts[venueId] = (venueCounts[venueId] || 0) + 1;
      return venueCounts[venueId] <= MAX_EVENTS_PER_VENUE;
    });

    // Step 2: Build result maintaining chronological order
    // Skip events that would create 3 consecutive cards from same venue
    const result = [];
    for (const event of capped) {
      if (result.length >= 25) break;

      const venueId = event.fields.Restaurant?.[0];
      // Check last 2 placed cards - if both are same venue, skip this one
      const lastTwo = result.slice(-2).map(e => e.fields.Restaurant?.[0]);
      const wouldBeThreeInARow = lastTwo.length === 2 &&
        lastTwo[0] === venueId &&
        lastTwo[1] === venueId;

      if (!wouldBeThreeInARow) {
        result.push(event);
      }
      // If it would be 3 in a row, skip it entirely (maintain chronological flow)
    }
    return result;
  })();

  // Get happy hour deals for carousel (unique venues with deals)
  const happyHourCarouselData = (() => {
    const venueDeals = new Map();

    deals.forEach(deal => {
      const venueId = deal.fields.Restaurant?.[0];
      if (!venueId) return;

      const venue = restaurants.find(r => r.id === venueId);
      if (!venue || venue.fields.Status !== 'Active') return;

      // Only add if venue has a thumbnail image
      const venueImage = venue.fields['Thumbnail Image']?.[0]?.url;
      if (!venueImage) return;

      // If we haven't seen this venue yet, add it
      if (!venueDeals.has(venueId)) {
        venueDeals.set(venueId, {
          venue,
          venueImage,
          dealName: deal.fields['Deal Name'],
          timeRange: formatTimeRange(deal.fields['Start Time'], deal.fields['End Time'])
        });
      }
    });

    return Array.from(venueDeals.values()).slice(0, 12);
  })();

  // Scroll to venue when clicking carousel card, optionally to a specific item
  const scrollToVenue = (venueId, expandSection = 'events', itemId = null) => {
    // Expand the specified section for that venue first
    setExpandedVenues(prev => ({
      ...prev,
      [venueId]: { ...prev[venueId], [expandSection]: true }
    }));

    // Use setTimeout to allow the section to expand before scrolling
    setTimeout(() => {
      // If we have a specific item ID, scroll to that item
      if (itemId) {
        const itemElement = document.getElementById(`item-${itemId}`);
        if (itemElement) {
          itemElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }
      // Otherwise scroll to the venue
      const element = document.getElementById(`venue-${venueId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // ============================================================================
  // VENUE SEARCH / AUTOCOMPLETE
  // ============================================================================

  // Filter restaurants based on search query
  const getSearchResults = () => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase().trim();
    return restaurants
      .filter(r => r.fields.Status === 'Active')
      .filter(r => {
        const name = (r.fields.Name || '').toLowerCase();
        return name.includes(query);
      })
      .slice(0, 6); // Limit to 6 results
  };

  // Handle selecting a venue from search results
  const handleSearchSelect = (venue) => {
    trackSearch(searchQuery, 1, market.id);
    setSearchQuery('');
    setShowSearchResults(false);
    // Scroll to venue and expand all sections that have content
    const element = document.getElementById(`venue-${venue.id}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Expand all sections for this venue
      setExpandedVenues(prev => ({
        ...prev,
        [venue.id]: { deals: true, specials: true, events: true }
      }));
    }
  };

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close market dropdown when clicking outside or pressing ESC
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (regionBarRef.current && !regionBarRef.current.contains(e.target)) {
        setMarketDropdownOpen(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') setMarketDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  const searchResults = getSearchResults();

  // ============================================================================
  // END VENUE SEARCH
  // ============================================================================

  // Format date for carousel cards (e.g., "Sat, Jan 25 @ 6:00 PM")
  const formatCarouselDate = (dateString) => {
    if (!dateString) return 'Date TBD';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Date TBD';

    // Include time if available (hours/minutes not both zero)
    const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0;
    if (hasTime) {
      const dateStr = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      return `${dateStr} @ ${timeStr}`;
    }
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Show floating clear button only when sticky bar chips scroll off screen
  useEffect(() => {
    if (activeFilterCount === 0) {
      setShowFloatingClear(false);
      return;
    }
    const handleScroll = () => {
      if (filterBarRef.current) {
        const rect = filterBarRef.current.getBoundingClientRect();
        // Show floating button when bottom of filter bar is above viewport
        setShowFloatingClear(rect.bottom < 0);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // check immediately
    return () => window.removeEventListener('scroll', handleScroll);
  });

  // ============================================================================
  // FILTER HELPERS
  // ============================================================================

  // Count active filters for badge
  // All default locations selected = not a user filter, so don't count those
  const allLocationsSelected = filters.where?.length === market.defaultCities.length &&
    market.defaultCities.every(city => filters.where.includes(city));
  const activeFilterCount = (filters.when?.length || 0) +
    (filters.what?.length || 0) +
    (allLocationsSelected ? 0 : (filters.where?.length || 0)) +
    (filters.happeningNow ? 1 : 0);

  // Remove a single filter chip
  const removeFilter = (type, id) => {
    setFilters(prev => ({
      ...prev,
      [type]: prev[type].filter(item => item !== id)
    }));
  };

  // Calculate filtered restaurant count for any given filter set.
  // IMPORTANT: This must use the same getXByRestaurant() functions as the main
  // render path to avoid count mismatches (e.g. dedup differences, filter gaps).
  // We temporarily swap `filters` via a ref so the getX functions see the preview
  // filter set from the FilterPanel, then restore it.
  const calculateResultCount = useCallback((filterSet) => {
    // Save current filters, temporarily apply the preview filterSet
    const savedFilters = filters;
    // We can't mutate state, but the getXByRestaurant functions read `filters`
    // from closure — so we need to replicate the main page's visibility logic
    // using the same checks. To keep it in sync, we mirror the visibleRestaurants
    // filter chain exactly.

    // Helper: apply happeningNow + WHEN filters to deals/specials (same as filterDeals)
    const previewFilterDeals = (items) => {
      let filtered = items;
      if (filterSet.happeningNow) {
        filtered = filtered.filter(isHappeningNow);
      }
      if (filterSet.when.length > 0) {
        const resolvedDays = [...new Set(filterSet.when.flatMap(resolveDayFilter))];
        filtered = filtered.filter(item =>
          item.fields['Days of Week']?.some(d => resolvedDays.includes(d))
        );
      }
      return filtered;
    };

    // Helper: apply happeningNow + WHEN filters to events (same as getEventsByRestaurant)
    const previewFilterEvents = (restaurantEvents) => {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const resolvedWhenDays = filterSet.when.length > 0
        ? [...new Set(filterSet.when.flatMap(resolveDayFilter))]
        : [];

      // Remove duplicates (same as getEventsByRestaurant)
      const seen = new Set();
      const uniqueEvents = restaurantEvents.filter(event => {
        const key = [
          event.fields['Event Name'],
          event.fields.Description,
          event.fields['Event Start'] || event.fields['Start Time'],
          event.fields['Event Type']
        ].join('|');
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      // Filter by date/day (same as getEventsByRestaurant)
      let filtered = uniqueEvents.filter(event => {
        if (event.fields.Recurring) {
          const eventDays = event.fields['Days of Week'] || [];
          if (resolvedWhenDays.length > 0) {
            return eventDays.some(d => resolvedWhenDays.includes(d));
          }
          return eventDays.length > 0;
        } else {
          if (!event.fields['Event Start']) return false;
          const eventDate = parseEventDate(event.fields['Event Start']);
          if (!eventDate || eventDate < today) return false;
          if (filterSet.when.length > 0) {
            const temporalIds = ['today', 'tomorrow', 'this_weekend'];
            const temporalFilters = filterSet.when.filter(id => temporalIds.includes(id));
            const dayNameFilters = filterSet.when.filter(id => !temporalIds.includes(id));
            if (temporalFilters.length > 0) {
              const dateRange = resolveWhenDateRange(temporalFilters);
              if (dateRange && eventDate >= dateRange.minDate && eventDate <= dateRange.maxDate) return true;
            }
            if (dayNameFilters.length > 0) {
              const eventDay = days[eventDate.getDay()];
              return dayNameFilters.includes(eventDay);
            }
            return false;
          }
          return true;
        }
      });

      // Apply happeningNow (same as getEventsByRestaurant)
      if (filterSet.happeningNow) {
        filtered = filtered.filter(isEventHappeningNow);
      }

      return filtered;
    };

    return restaurants
      .filter(restaurant => {
        if (restaurant.fields.Status !== 'Active') return false;
        if (filterSet.where.length > 0) {
          const cityValue = restaurant.fields.City?.trim() || '';
          if (!filterSet.where.some(loc => loc.toLowerCase() === cityValue.toLowerCase())) return false;
        }
        return true;
      })
      .filter(restaurant => {
        // Get deals with dedup (mirrors getDealsByRestaurant logic)
        const rawDeals = deals.filter(d => d.fields.Restaurant && d.fields.Restaurant.includes(restaurant.id));
        const rawSpecials = specials.filter(s => s.fields.Restaurant && s.fields.Restaurant.includes(restaurant.id));
        const rawEvents = events.filter(e => e.fields.Restaurant && e.fields.Restaurant.includes(restaurant.id));

        const filteredDeals = previewFilterDeals(rawDeals);
        const filteredSpecials = previewFilterDeals(rawSpecials);
        const filteredEvents = previewFilterEvents(rawEvents);

        const hasContent = filteredDeals.length > 0 || filteredSpecials.length > 0 || filteredEvents.length > 0;
        if (!hasContent) return false;

        // Apply WHAT filter
        if (filterSet.what.length > 0) {
          const hasHappyHour = filterSet.what.includes('happy_hour') && filteredDeals.length > 0;
          const hasSpecials = filterSet.what.includes('specials') && filteredSpecials.length > 0;
          const eventTypeFilters = filterSet.what.filter(w => w !== 'happy_hour' && w !== 'specials');
          const hasMatchingEvent = eventTypeFilters.length > 0 && filteredEvents.some(evt =>
            eventTypeFilters.some(f => (evt.fields['Event Type'] || '').toLowerCase().includes(f.toLowerCase()))
          );
          if (!hasHappyHour && !hasSpecials && !hasMatchingEvent) return false;
        }
        return true;
      }).length;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurants, deals, specials, events]);

  // Calculate filtered restaurant count for current applied filters
  const filteredRestaurantCount = calculateResultCount(filters);

  // Auto-expand matching sections when filters change
  // Start fresh (collapse all) then only expand what matches
  useEffect(() => {
    // Reset event pagination when filters change
    setEventPages({});

    // WHEN-only filter: auto-expand events for venues with matching events
    if (filters.when.length > 0 && filters.what.length === 0 && !filters.happeningNow) {
      const newExpanded = {};
      restaurants.forEach(restaurant => {
        const restaurantEvents = getEventsByRestaurant(restaurant.id);
        if (restaurantEvents.length > 0) {
          newExpanded[restaurant.id] = { events: true };
        }
      });
      setExpandedVenues(newExpanded);
      return;
    }

    if (filters.what.length === 0 && !filters.happeningNow) {
      // No WHAT filters and no Happening Now — collapse everything
      setExpandedVenues({});
      return;
    }

    const eventTypeFilters = filters.what.filter(w => w !== 'happy_hour' && w !== 'specials');
    const wantDeals = filters.what.includes('happy_hour');
    const wantSpecials = filters.what.includes('specials');

    // Build fresh expanded state — only expand sections that match the filter
    const newExpanded = {};
    restaurants.forEach(restaurant => {
      const sections = {};

      if (eventTypeFilters.length > 0) {
        const restaurantEvents = getEventsByRestaurant(restaurant.id);
        const hasMatch = restaurantEvents.some(evt =>
          eventTypeFilters.some(f => (evt.fields['Event Type'] || '').toLowerCase().includes(f.toLowerCase()))
        );
        if (hasMatch) sections.events = true;
      }

      if (wantDeals || filters.happeningNow) {
        const restaurantDeals = getDealsByRestaurant(restaurant.id);
        if (filters.happeningNow) {
          // Only expand if deals are actually happening now
          if (restaurantDeals.some(isHappeningNow)) sections.deals = true;
        } else if (restaurantDeals.length > 0) {
          sections.deals = true;
        }
      }

      if (wantSpecials) {
        const restaurantSpecials = getSpecialsByRestaurant(restaurant.id);
        if (restaurantSpecials.length > 0) sections.specials = true;
      }

      // Happening Now: expand events only if any are actually happening now
      // (getEventsByRestaurant already filters by happeningNow when active)
      if (filters.happeningNow && !eventTypeFilters.length) {
        const restaurantEvents = getEventsByRestaurant(restaurant.id);
        if (restaurantEvents.length > 0) sections.events = true;
      }

      if (Object.keys(sections).length > 0) {
        newExpanded[restaurant.id] = sections;
      }
    });
    setExpandedVenues(newExpanded);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.what, filters.when, filters.happeningNow]);

  if (loading) {
    return <div className="container"><div className="loading">Loading...</div></div>;
  }

  if (error) {
    return <div className="container"><div className="error">{error}</div></div>;
  }

  return (
    <div
      className="App"
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <SEOHead
        title={`${market.regionLabel} — Happy Hours & Events`}
        description={`Discover happy hour deals, daily specials, trivia, bingo, live music and events in ${market.cities.join(', ')}.`}
        market={market}
      />
        {/* Pull-to-refresh indicator */}
        <div
          className="pull-to-refresh-indicator"
          style={{
            height: pullDistance,
            opacity: pullDistance / PULL_THRESHOLD
          }}
        >
          {isRefreshing ? (
            <span className="refresh-spinner">↻</span>
          ) : (
            <span>{pullDistance >= PULL_THRESHOLD ? '↻ Release to refresh' : '↓ Pull to refresh'}</span>
          )}
        </div>

        <header className="header">
          {market.headerLayout === 'centered' ? (
            <div className="header-inner header-centered">
              <img src={market.headerLogo || market.logo} alt={market.logoAlt} className="header-logo-centered" />
              {market.headerTagline && <p className="header-tagline">{market.headerTagline}</p>}
            </div>
          ) : (
            <div className="header-inner">
              {market.websiteUrl ? (
                <a href={market.websiteUrl} target="_blank" rel="noopener noreferrer">
                  <img src={market.headerLogo || market.logo} alt={market.logoAlt} className="header-logo" />
                </a>
              ) : (
                <img src={market.headerLogo || market.logo} alt={market.logoAlt} className="header-logo" />
              )}
              <div className="header-content">
                <h1>{market.name}</h1>
                <p>{market.headerTagline || market.tagline}</p>
              </div>
            </div>
          )}
          <HamburgerMenu />
        </header>

        {market.showRegionBar && market.regionLabel && (
          <div className="region-bar" ref={regionBarRef}>
            {market.regionDescriptor && (
              <span className="region-bar-descriptor">{market.regionDescriptor}</span>
            )}
            <button className="region-bar-market" onClick={() => setMarketDropdownOpen(prev => !prev)}>
              <span className="region-bar-icon">📍</span>
              <span className="region-bar-label">{market.regionLabel}</span>
              <span className={`region-bar-chevron ${marketDropdownOpen ? 'region-bar-chevron-open' : ''}`}>▾</span>
            </button>
            {marketDropdownOpen && (
              <div className="region-dropdown">
                {getSwitcherMarkets(market.id).map(m => (
                  m.isCurrent ? (
                    <div key={m.id} className="region-dropdown-item region-dropdown-current">
                      <span className="region-dropdown-icon">📍</span>
                      <span className="region-dropdown-label">{m.regionLabel}</span>
                      <span className="region-dropdown-check">✓</span>
                    </div>
                  ) : (
                    <div
                      key={m.id}
                      className="region-dropdown-item"
                      onClick={() => {
                        trackMarketSwitched(market.id, m.id);
                        if (m.isPathMode) {
                          // Update saved preference so root URL redirects to this market next time
                          localStorage.setItem('otc_preferred_market', m.slug);
                          rawNavigate(m.url);
                        } else {
                          window.location.href = m.url;
                        }
                      }}
                      role="link"
                    >
                      <span className="region-dropdown-icon">📍</span>
                      <span className="region-dropdown-label">{m.regionLabel}</span>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        )}

        {/* Events Carousel - Toggle via Airtable Settings */}
        {showEventsCarousel && upcomingEvents.length > 0 && (
          <section className="upcoming-events-banner">
            <h3>Upcoming Events:</h3>
            <AutoScrollCarousel
              items={upcomingEvents}
              autoScroll={carouselAutoScroll}
              scrollSpeed={carouselScrollSpeed}
              scaleMax={carouselScaleMax}
              friction={carouselFriction}
              cardWidth={window.innerWidth <= 768 ? 220 : 280}
              renderCard={(event) => {
                const venue = restaurants.find(r => r.id === event.fields.Restaurant?.[0]);
                const venueImage = venue?.fields['Thumbnail Image']?.[0]?.url;
                const eventType = event.fields['Event Type'];
                const categoryImage = getEventCategoryImage(eventType);
                const scrapedImage = event.fields['Event_Image']?.[0]?.url;

                // Fallback chain: scraped image → category image → venue thumbnail → placeholder
                const eventImage = scrapedImage || categoryImage;

                return (
                  <div
                    className="carousel-event-card"
                    onClick={() => {
                      gaEvent('carousel_click', {
                        carousel_type: 'events',
                        item_name: event.fields['Event Name'],
                        venue_name: venue?.fields?.Name || 'Unknown',
                        market_id: market.id,
                      });
                      scrollToVenue(venue?.id, 'events', event.id);
                    }}
                  >
                    {venue?.fields.City && (
                      <span className="carousel-city-tag">{venue.fields.City}</span>
                    )}
                    <img
                      src={eventImage}
                      alt={event.fields['Event Name']}
                      onError={(e) => {
                        // Fallback chain: venue thumbnail → placeholder
                        // Track failed sources to prevent infinite loops
                        const failedSrcs = e.target.dataset.failedSrcs ? e.target.dataset.failedSrcs.split(',') : [];
                        const currentSrc = e.target.src;
                        failedSrcs.push(currentSrc);
                        e.target.dataset.failedSrcs = failedSrcs.join(',');

                        // Try venue image if available and not already failed
                        if (venueImage && !failedSrcs.includes(venueImage)) {
                          e.target.src = venueImage;
                        } else {
                          // All fallbacks exhausted - use placeholder
                          e.target.onerror = null;
                          e.target.src = `/images/${market.id === 'tcc' ? '' : `${market.id}-`}placeholder-event.svg`;
                        }
                      }}
                    />
                    <div className="carousel-event-info">
                      <span className="carousel-event-name">{formatTextCase(event.fields['Event Name'])}</span>
                      <span className="carousel-event-date">{formatCarouselDate(event.fields['Event Start'])}</span>
                      <span className="carousel-venue-name">{venue?.fields.Name || 'Venue TBD'}</span>
                    </div>
                  </div>
                );
              }}
            />
          </section>
        )}

        {/* Happy Hour Carousel - Toggle via Airtable Settings */}
        {showHappyHourCarousel && happyHourCarouselData.length > 0 && (
          <section className="upcoming-events-banner happyhour-carousel-banner">
            <h3>Happy Hours:</h3>
            <AutoScrollCarousel
              items={happyHourCarouselData}
              autoScroll={carouselAutoScroll}
              scrollSpeed={carouselScrollSpeed}
              scaleMax={carouselScaleMax}
              friction={carouselFriction}
              cardWidth={window.innerWidth <= 768 ? 220 : 280}
              renderCard={(item) => (
                <div
                  className="carousel-event-card"
                  onClick={() => {
                    gaEvent('carousel_click', {
                      carousel_type: 'happy_hour',
                      item_name: item.dealName,
                      venue_name: item.venue.fields.Name,
                      market_id: market.id,
                    });
                    scrollToVenue(item.venue.id, 'deals');
                  }}
                >
                  {item.venue?.fields.City && (
                    <span className="carousel-city-tag">{item.venue.fields.City}</span>
                  )}
                  <img
                    src={item.venueImage}
                    alt={item.venue.fields.Name}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `/images/${market.id === 'tcc' ? '' : `${market.id}-`}placeholder-event.svg`;
                    }}
                  />
                  <div className="carousel-event-info">
                    <span className="carousel-event-name">{item.dealName}</span>
                    <span className="carousel-event-date">{item.timeRange}</span>
                    <span className="carousel-venue-name">{item.venue.fields.Name}</span>
                  </div>
                </div>
              )}
            />
          </section>
        )}

      {/* Sticky Filter Bar */}
      <div className="sticky-filter-bar" ref={filterBarRef}>
        <div className="filter-bar-row">
          {/* Search */}
          <div className="search-container" ref={searchRef} style={{ flex: 1 }}>
            <div className="search-input-wrapper">
              <span className="search-icon">&#128269;</span>
              <input
                type="text"
                className="search-input"
                placeholder="Search venues..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSearchResults(true);
                }}
                onFocus={() => setShowSearchResults(true)}
              />
              {searchQuery && (
                <button
                  className="search-clear"
                  onClick={() => {
                    setSearchQuery('');
                    setShowSearchResults(false);
                  }}
                >
                  ×
                </button>
              )}
            </div>
            {showSearchResults && searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map(venue => (
                  <button
                    key={venue.id}
                    className="search-result-item"
                    onClick={() => handleSearchSelect(venue)}
                  >
                    <span className="search-result-name">{venue.fields.Name}</span>
                    <span className="search-result-city">{venue.fields.City}</span>
                  </button>
                ))}
              </div>
            )}
            {showSearchResults && searchQuery && searchResults.length === 0 && (
              <div className="search-results">
                <div className="search-no-results">No venues found</div>
              </div>
            )}
          </div>

          {/* Filter Button */}
          <button
            className={`filter-trigger-btn ${activeFilterCount > 0 ? 'has-filters' : ''}`}
            onClick={() => setFilterPanelOpen(true)}
          >
            <span className="filter-trigger-icon">☰</span>
            Filters
            {activeFilterCount > 0 && (
              <span className="filter-badge">{activeFilterCount}</span>
            )}
          </button>
        </div>

        {/* Active filter chips */}
        {activeFilterCount > 0 && (
          <div className="active-filter-chips">
            {filters.when.map(id => (
              <button key={`when-${id}`} className="active-filter-chip" onClick={() => removeFilter('when', id)}>
                {getFilterLabel('when', id)} <span className="active-filter-chip-x">×</span>
              </button>
            ))}
            {filters.what.map(id => (
              <button key={`what-${id}`} className="active-filter-chip" onClick={() => removeFilter('what', id)}>
                {getFilterLabel('what', id)} <span className="active-filter-chip-x">×</span>
              </button>
            ))}
            {!allLocationsSelected && filters.where.map(id => (
              <button key={`where-${id}`} className="active-filter-chip" onClick={() => removeFilter('where', id)}>
                {getFilterLabel('where', id)} <span className="active-filter-chip-x">×</span>
              </button>
            ))}
            {filters.happeningNow && (
              <button className="active-filter-chip" onClick={() => setFilters(prev => ({ ...prev, happeningNow: false }))}>
                Happening Now <span className="active-filter-chip-x">×</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Filter Panel (bottom sheet) */}
      <FilterPanel
        isOpen={filterPanelOpen}
        onClose={() => setFilterPanelOpen(false)}
        filters={filters}
        onApplyFilters={(newFilters) => {
          setFilters(newFilters);
          if (newFilters.when?.length) trackFilterApplied('when', newFilters.when, market.id);
          if (newFilters.what?.length) trackFilterApplied('what', newFilters.what, market.id);
          if (newFilters.where?.length && newFilters.where.join(',') !== market.defaultCities.join(',')) {
            trackFilterApplied('where', newFilters.where, market.id);
          }
        }}
        calculateResultCount={calculateResultCount}
        locations={market.cities.map(city => ({ id: city, label: city }))}
      />

      <main className="container">
        <section className="restaurants">
          {(() => {
            // Helper to get content stats for a restaurant
            // Returns: { categoryCount: 0-3, totalCount: number }
            // categoryCount = how many content types the venue has (Happy Hours, Specials, Events)
            // totalCount = total number of items across all categories
            const getContentStats = (restaurantId) => {
              const dealCount = deals.filter(d => d.fields.Restaurant?.includes(restaurantId)).length;
              const specialCount = specials.filter(s => s.fields.Restaurant?.includes(restaurantId)).length;
              const eventCount = events.filter(e => e.fields.Restaurant?.includes(restaurantId)).length;

              // Count how many categories have content (0-3)
              const categoryCount = (dealCount > 0 ? 1 : 0) + (specialCount > 0 ? 1 : 0) + (eventCount > 0 ? 1 : 0);

              return {
                categoryCount,
                totalCount: dealCount + specialCount + eventCount
              };
            };

            // Build the list of visible restaurants, then interleave ads
            const visibleRestaurants = restaurants
              .filter(restaurant => {
                if (restaurant.fields.Status !== 'Active') return false;
                // WHERE filter
                if (filters.where.length > 0) {
                  const cityValue = restaurant.fields.City?.trim() || '';
                  if (!filters.where.some(loc => loc.toLowerCase() === cityValue.toLowerCase())) return false;
                }
                return true;
              })
              .filter(restaurant => {
                const restaurantDeals = getDealsByRestaurant(restaurant.id);
                const restaurantSpecials = getSpecialsByRestaurant(restaurant.id);
                const restaurantEvents = getEventsByRestaurant(restaurant.id);
                const hasContent = restaurantDeals.length > 0 || restaurantSpecials.length > 0 || restaurantEvents.length > 0;
                if (!hasContent) return false;

                // Happening Now filter - only show venues with something actually happening now
                // All three getXByRestaurant() functions already filter by happeningNow when active
                if (filters.happeningNow) {
                  const hasDealsNow = restaurantDeals.length > 0;
                  const hasSpecialsNow = restaurantSpecials.length > 0;
                  const hasEventsNow = restaurantEvents.length > 0;
                  if (!hasDealsNow && !hasSpecialsNow && !hasEventsNow) return false;
                }

                // WHAT filter - filter venues by content type
                if (filters.what.length > 0) {
                  const hasHappyHour = filters.what.includes('happy_hour') && restaurantDeals.length > 0;
                  const hasSpecials = filters.what.includes('specials') && restaurantSpecials.length > 0;
                  // Check for specific event types
                  const eventTypeFilters = filters.what.filter(w => w !== 'happy_hour' && w !== 'specials');
                  const hasMatchingEvent = eventTypeFilters.length > 0 && restaurantEvents.some(evt =>
                    eventTypeFilters.some(f => (evt.fields['Event Type'] || '').toLowerCase().includes(f.toLowerCase()))
                  );
                  // Venue must match at least one WHAT filter
                  if (!hasHappyHour && !hasSpecials && !hasMatchingEvent) return false;
                }

                return true;
              })
              // Sort: Tier → Category Coverage → Total Content → Alphabetical
              .sort((a, b) => {
                // 1. Tier field (Platinum > Gold > Silver > Basic) - for paid/featured spots
                const tierOrder = { 'Platinum': 4, 'Gold': 3, 'Silver': 2, 'Basic': 1 };
                const tierA = tierOrder[a.fields.Tier] || 0;
                const tierB = tierOrder[b.fields.Tier] || 0;
                if (tierB !== tierA) return tierB - tierA;

                // 2. Priority number (higher = more prominent) within same Tier
                const priorityA = a.fields.Priority || 0;
                const priorityB = b.fields.Priority || 0;
                if (priorityB !== priorityA) return priorityB - priorityA;

                // 3. Category coverage (venues with all 3 types > 2 types > 1 type)
                // A venue with Happy Hours + Specials + Events ranks higher than one with just Events
                const statsA = getContentStats(a.id);
                const statsB = getContentStats(b.id);
                if (statsB.categoryCount !== statsA.categoryCount) {
                  return statsB.categoryCount - statsA.categoryCount;
                }

                // 4. Total content count as tiebreaker within same category coverage
                if (statsB.totalCount !== statsA.totalCount) {
                  return statsB.totalCount - statsA.totalCount;
                }

                // 5. Alphabetical tiebreaker
                return (a.fields.Name || '').localeCompare(b.fields.Name || '');
              });

            const elements = [];
            let adSlotIndex = 0;

            visibleRestaurants.forEach((restaurant, index) => {
              // Insert ad before this restaurant if it's the right position
              if (advertisingEnabled && index > 0 && index % adFrequency === 0) {
                const ad = getAdForSlot(adSlotIndex);
                if (ad) {
                  elements.push(<AdBanner key={`ad-${adSlotIndex}`} ad={ad} />);
                  adSlotIndex++;
                }
              }

              // Render restaurant card
              elements.push((() => {
            const restaurantDeals = getDealsByRestaurant(restaurant.id);
            const restaurantSpecials = getSpecialsByRestaurant(restaurant.id);
            const restaurantEvents = getEventsByRestaurant(restaurant.id);

            const venueImage = restaurant.fields['Thumbnail Image']?.[0]?.url;

            // WHAT filter: determine which event types to show
            const eventTypeFilters = filters.what.filter(w => w !== 'happy_hour' && w !== 'specials');

            // Filter events to only matching types when WHAT filter has event types
            let matchingEvents = eventTypeFilters.length > 0
              ? restaurantEvents.filter(evt =>
                  eventTypeFilters.some(f => (evt.fields['Event Type'] || '').toLowerCase().includes(f.toLowerCase()))
                )
              : restaurantEvents;

            // Note: Happening Now filter is already applied inside getEventsByRestaurant()

            // When WHAT filters are active, only show first matching event
            // unless user has clicked "See all events" (tracked via expandedVenues.allEvents)
            const hasWhatFilter = filters.what.length > 0;
            const showingAllEvents = expandedVenues[restaurant.id]?.allEvents;
            const filteredEvents = (hasWhatFilter && !showingAllEvents)
              ? matchingEvents.slice(0, 1)
              : matchingEvents;
            const hasMoreEvents = hasWhatFilter && matchingEvents.length > filteredEvents.length;

            // Paginate events: show 4 at a time (skip pagination when WHEN-only filter is active —
            // show all events that match the time window, e.g. all Fri/Sat/Sun for "This Weekend")
            const EVENT_PAGE_SIZE = 4;
            const hasWhenOnlyFilter = filters.when.length > 0 && filters.what.length === 0 && !filters.happeningNow;
            const currentEventPages = eventPages[restaurant.id] || 1;
            const visibleEvents = hasWhenOnlyFilter
              ? filteredEvents
              : filteredEvents.slice(0, EVENT_PAGE_SIZE * currentEventPages);
            const hasMorePaginated = filteredEvents.length > visibleEvents.length;

            // WHEN-only filter: show upcoming events beyond the filter window
            const upcomingEvents = getUpcomingEventsByRestaurant(restaurant.id);
            const hasUpcomingBeyondFilter = upcomingEvents.length > 0;
            const showingUpcoming = expandedVenues[restaurant.id]?.upcomingEvents;

            // Paginate upcoming events too
            const upcomingPages = eventPages[`${restaurant.id}_upcoming`] || 1;
            const visibleUpcoming = upcomingEvents.slice(0, EVENT_PAGE_SIZE * upcomingPages);
            const hasMoreUpcomingPaginated = upcomingEvents.length > visibleUpcoming.length;

            return (
              <div key={restaurant.id} id={`venue-${restaurant.id}`} className="restaurant-card">
                <div className="restaurant-main">
                  {venueImage && (
                    <img src={venueImage} alt={restaurant.fields.Name} className="venue-image" />
                  )}

                  <div className="restaurant-info">
                    <div className="restaurant-header">
                      <h3>{restaurant.fields.Name}</h3>
                      <span className="restaurant-type">{restaurant.fields.Type}</span>
                      {(restaurant.fields.is_featured || restaurant.fields.Tier === 'Gold' || restaurant.fields.Tier === 'Platinum') && (
                        <span className="featured-badge">Featured</span>
                      )}
                    </div>

                    {restaurant.fields.Description && (
                      <p className="venue-description">{renderMarkdown(restaurant.fields.Description)}</p>
                    )}

                    {restaurant.fields.Address && (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurant.fields.Address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="address-link"
                      >
                        📍 {restaurant.fields.Address}
                      </a>
                    )}

                    {restaurant.fields.Website && (
                      <a
                        href={restaurant.fields.Website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="website-link"
                      >
                        Visit Website →
                      </a>
                    )}

                    {/* Silver/Platinum tier: phone + social icons */}
                    {(restaurant.fields.Tier === 'Silver' || restaurant.fields.Tier === 'Platinum') && (
                      (restaurant.fields['Phone Number'] || restaurant.fields['Facebook URL'] || restaurant.fields['Instagram URL']) && (
                        <div className="venue-contact-row">
                          {restaurant.fields['Phone Number'] && (
                            <a href={`tel:${restaurant.fields['Phone Number']}`} className="venue-contact-link" title="Call">
                              <svg className="venue-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                              <span>{restaurant.fields['Phone Number']}</span>
                            </a>
                          )}
                          <div className="venue-social-icons">
                            {restaurant.fields['Facebook URL'] && (
                              <a href={restaurant.fields['Facebook URL']} target="_blank" rel="noopener noreferrer" className="venue-social-btn venue-social-fb" title="Facebook">
                                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 1.09.07 1.373.14V7.91a9 9 0 0 0-.73-.024c-1.032 0-1.432.392-1.432 1.414v2.744h2.08l-.357 3.667h-1.723v8.15C19.396 23.2 24 18.148 24 12.073 24 5.445 18.627.073 12 .073S0 5.445 0 12.073c0 5.384 3.692 9.905 8.682 11.182q.21.04.419.436"/></svg>
                              </a>
                            )}
                            {restaurant.fields['Instagram URL'] && (
                              <a href={restaurant.fields['Instagram URL']} target="_blank" rel="noopener noreferrer" className="venue-social-btn venue-social-ig" title="Instagram">
                                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M7.0301.084c-1.2768.0602-2.1487.264-2.911.5634-.7888.3075-1.4575.72-2.1228 1.3877-.6652.6677-1.075 1.3368-1.3802 2.127-.2954.7638-.4956 1.6365-.552 2.914-.0564 1.2775-.0689 1.6882-.0626 4.947.0062 3.2586.0206 3.6671.0825 4.9473.061 1.2765.264 2.1482.5635 2.9107.308.7889.72 1.4573 1.388 2.1228.6679.6655 1.3365 1.0743 2.1285 1.38.7632.295 1.6361.4961 2.9134.552 1.2773.056 1.6884.069 4.9462.0627 3.2578-.0062 3.668-.0207 4.9478-.0814 1.28-.0607 2.147-.2652 2.9098-.5633.7889-.3086 1.4578-.72 2.1228-1.3881.665-.6682 1.0745-.3378 1.3795-2.1284.2957-.7632.4966-1.636.552-2.9124.056-1.2809.0692-1.6898.063-4.948-.0063-3.2583-.021-3.6668-.0817-4.9465-.0607-1.2797-.264-2.1487-.5633-2.9117-.3084-.7889-.72-1.4568-1.3876-2.1228C21.2982 1.33 20.628.9208 19.8504.6165 19.0872.321 18.2143.12 16.9366.0667 15.6589.0093 15.2482-.005 12.0014-.0014 8.7544.0048 8.3456.019 7.065.0795zm.217 21.6858c-1.1704-.0535-1.8063-.2497-2.2286-.4152-.5604-.218-.96-.4778-1.3806-.8894-.4227-.4145-.6744-.8186-.8903-1.3803-.164-.4224-.3583-1.0581-.4086-2.2288-.0543-1.2664-.0654-1.6462-.0617-4.8507.0037-3.2042.0179-3.579.0766-4.8434.0535-1.1704.2497-1.8063.4152-2.2286.2181-.5604.4778-.96.8894-1.3806.4145-.4227.8186-.6744 1.3803-.8903.4224-.164 1.0581-.3583 2.2288-.4087 1.2664-.0543 1.6462-.0654 4.8507-.0617 3.2045.0037 3.5793.018 4.8438.0766 1.1703.0536 1.8063.2498 2.2286.4152.5604.218.96.4778 1.3806.8894.4227.4145.6744.8186.8903 1.3803.164.4224.3583 1.0581.4086 2.2288.0543 1.2664.0654 1.6462.0617 4.8507-.0037 3.2042-.0179 3.579-.0766 4.8434-.0535 1.1704-.2497 1.8063-.4152 2.2286-.218.5604-.4778.96-.8894 1.3806-.4145.4227-.8186.6744-1.3803.8903-.4224.164-1.0581.3583-2.2288.4087-1.2664.0543-1.6462.0654-4.8507.0617-3.2042-.0037-3.579-.0179-4.8434-.0766zM12.0068 5.8376a6.1628 6.1628 0 1 0-.001 12.326 6.1628 6.1628 0 0 0 .001-12.326zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>
                              </a>
                            )}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>

                {/* Happy Hour Section - Grouped by Category */}
                {restaurantDeals.length > 0 && (
                  <div className="expandable-section">
                    <button
                      className="section-toggle"
                      onClick={() => toggleVenueSection(restaurant.id, 'deals')}
                    >
                      <span className="section-title">
                        <img src="/images/happy-hour-icon.png" alt="Happy Hour" className="section-icon" onError={(e) => e.target.style.display = 'none'} />
                        <span>Happy Hour</span>
                      </span>
                      <span className="toggle-icon">
                        {expandedVenues[restaurant.id]?.deals ? '▼' : '▶'}
                      </span>
                    </button>

                    {expandedVenues[restaurant.id]?.deals && (() => {
                      const scheduleGroups = groupDealsBySchedule(restaurantDeals);
                      const isSingleSchedule = scheduleGroups.length === 1;

                      // Render deals within a schedule group (categorized)
                      const renderScheduleDeals = (groupDeals) => {
                        const grouped = groupDealsByCategory(groupDeals);
                        const hasMultipleCats =
                          (grouped.food.length > 0 ? 1 : 0) +
                          (grouped.drinks.length > 0 ? 1 : 0) +
                          (grouped.other.length > 0 ? 1 : 0) > 1;

                        const renderCatDeals = (catDeals, label, icon) => {
                          if (catDeals.length === 0) return null;
                          return (
                            <div className="deal-category-section" key={label}>
                              {hasMultipleCats && (
                                <div className="deal-category-header">
                                  <span className="category-icon">{icon}</span>
                                  <span className="category-label">{label}</span>
                                </div>
                              )}
                              <div className="deal-category-items">
                                {catDeals.map((deal, idx) => {
                                  const dealName = deal.fields['Deal Name'] || '';
                                  const isRedundantName = /happy\s*hour/i.test(dealName) && scheduleGroups.length > 0;
                                  const showTitle = deal.fields['Hide Title'] !== true;
                                  const desc = deal.fields.Description || '';

                                  if (deal.parsedItems && deal.parsedItems.length >= 1) {
                                    const showDealName = showTitle && !deal.originalCategory && !isRedundantName && dealName && (deal.parsedItems.length > 1 || showTitle);
                                    return (
                                      <div key={deal.id + '-' + idx} className="deal deal-with-items">
                                        {showDealName && (
                                          <strong className="deal-name">{dealName}</strong>
                                        )}
                                        {deal.parsedItems.map((item, itemIdx) => (
                                          <React.Fragment key={itemIdx}>
                                            <ContentItem
                                              name={item.item}
                                              price={item.price}
                                              description={item.description}
                                              noBorder={itemIdx === deal.parsedItems.length - 1 && !item.children?.length}
                                            />
                                            {item.children && item.children.map((child, ci) => (
                                              <div key={`${itemIdx}-sub-${ci}`} className="ci-subitem">{child}</div>
                                            ))}
                                          </React.Fragment>
                                        ))}
                                      </div>
                                    );
                                  }

                                  // No parsed items — render name + description as a single ContentItem
                                  return (
                                    <div key={deal.id} className="deal deal-with-items">
                                      <ContentItem
                                        name={dealName || desc}
                                        price={extractPrice(desc)}
                                        description={dealName ? desc : null}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        };

                        return (
                          <>
                            {renderCatDeals(grouped.drinks, 'Drinks', '🍺')}
                            {renderCatDeals(grouped.food, 'Food', '🍽️')}
                            {renderCatDeals(grouped.other, 'Specials', '⭐')}
                          </>
                        );
                      };

                      return (
                        <div className="section-content">
                          {scheduleGroups.map((group, groupIdx) => (
                            <div key={groupIdx} className={`schedule-group ${!isSingleSchedule && groupIdx > 0 ? 'schedule-group-separator' : ''}`}>
                              <div className="shared-schedule-header">
                                <span className="time">
                                  <span className="time-emoji">🕐</span>
                                  {group.time}
                                </span>
                                <span className="days">
                                  <img src="/images/date_icon.png" alt="Date" className="date-icon" onError={(e) => e.target.style.display = 'none'} />
                                  {group.daysLabel.replace(/\b\w/g, c => c.toUpperCase())}
                                </span>
                              </div>
                              {renderScheduleDeals(group.deals)}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Restaurant Specials Section */}
                {restaurantSpecials.length > 0 && (
                  <div className="expandable-section">
                    <button
                      className="section-toggle"
                      onClick={() => toggleVenueSection(restaurant.id, 'specials')}
                    >
                      <span className="section-title">
                        <img src="/images/specials-icon.png" alt="Daily Specials" className="section-icon" onError={(e) => e.target.style.display = 'none'} />
                        <span>Daily Specials</span>
                      </span>
                      <span className="toggle-icon">
                        {expandedVenues[restaurant.id]?.specials ? '▼' : '▶'}
                      </span>
                    </button>

                    {expandedVenues[restaurant.id]?.specials && (
                      <div className="section-content">
                        {groupSpecialsByDays(restaurantSpecials).map((group, groupIndex) => (
                          <div key={groupIndex} className="specials-group">
                            <div className="specials-group-header">
                              <img src="/images/date_icon.png" alt="Date" className="date-icon" onError={(e) => e.target.style.display = 'none'} />
                              <span>{group.label.replace(/\b\w/g, c => c.toUpperCase())}</span>
                            </div>
                            <div className="specials-list">
                              {group.specials.map(special => {
                                const desc = special.fields.Description || '';
                                const showTitle = special.fields['Hide Title'] !== true;
                                const specialName = special.fields['Special Name'] || '';
                                const parsedItems = parseItemsFromDescription(desc);

                                if (parsedItems.length >= 1) {
                                  return (
                                    <div key={special.id} className="deal deal-with-items">
                                      {showTitle && specialName && <strong className="deal-name">{specialName}</strong>}
                                      {parsedItems.map((item, ii) => (
                                        <ContentItem key={ii} name={item.item} price={item.price} description={item.description || null} noBorder={ii === parsedItems.length - 1} />
                                      ))}
                                    </div>
                                  );
                                }

                                return (
                                  <ContentItem
                                    key={special.id}
                                    name={showTitle ? specialName : ''}
                                    price={extractPrice(desc)}
                                    description={desc}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Events Section */}
                {filteredEvents.length > 0 && (
                  <div className="expandable-section">
                    <button
                      className="section-toggle"
                      onClick={() => toggleVenueSection(restaurant.id, 'events')}
                    >
                      <span className="section-title">
                        <img src="/images/events-icon.png" alt="Events" className="section-icon" onError={(e) => e.target.style.display = 'none'} />
                        <span>Events{hasWhatFilter ? ` (${matchingEvents.length})` : ''}</span>
                      </span>
                      <span className="toggle-icon">
                        {expandedVenues[restaurant.id]?.events ? '▼' : '▶'}
                      </span>
                    </button>

                    {expandedVenues[restaurant.id]?.events && (
                      <div className="section-content">
                        {visibleEvents.map(event => {
                          const scrapedImage = event.fields['Event_Image']?.[0]?.url;
                          const eventType = event.fields['Event Type'];
                          const categoryImage = getEventCategoryImage(eventType);
                          const venueImage = restaurant.fields['Thumbnail Image']?.[0]?.url;
                          // Always show an image - use category fallback if no scraped image
                          const displayImage = scrapedImage || categoryImage;
                          return (
                            <div key={event.id} id={`item-${event.id}`} className="event event-with-image">
                              <img
                                src={displayImage}
                                alt={event.fields['Event Name']}
                                className="event-image"
                                onError={(e) => {
                                  // Fallback chain: venue thumbnail → hide
                                  // Track failed sources to prevent infinite loops
                                  const failedSrcs = e.target.dataset.failedSrcs ? e.target.dataset.failedSrcs.split(',') : [];
                                  const currentSrc = e.target.src;
                                  failedSrcs.push(currentSrc);
                                  e.target.dataset.failedSrcs = failedSrcs.join(',');

                                  // Try venue image if available and not already failed
                                  if (venueImage && !failedSrcs.includes(venueImage)) {
                                    e.target.src = venueImage;
                                  } else {
                                    // All fallbacks exhausted - hide image
                                    e.target.onerror = null;
                                    e.target.style.display = 'none';
                                    e.target.parentElement.classList.remove('event-with-image');
                                  }
                                }}
                              />
                              <div className="event-content">
                                <div className="event-header">
                                  <strong>{formatTextCase(event.fields['Event Name'])}</strong>
                                  {event.fields['Event Start'] && parseEventDate(event.fields['Event Start']) && (
                                    <button
                                      className="add-to-calendar-btn"
                                      onClick={() => openCalendarModal(event, restaurant.fields.Name, restaurant.fields.Address)}
                                      title="Add to Calendar"
                                    >
                                      + Calendar
                                    </button>
                                  )}
                                </div>
                                <p>{renderMarkdown(event.fields.Description)}</p>
                                <div className="event-details">
                                  <span className="event-type">{event.fields['Event Type']}</span>
                                  <span className="event-date">
                                    <img src="/images/date_icon.png" alt="Date" className="date-icon" onError={(e) => e.target.style.display = 'none'} />
                                    {formatEventDate(event)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {hasMorePaginated && (
                          <button
                            className="see-all-events-btn"
                            onClick={() => {
                              setEventPages(prev => ({
                                ...prev,
                                [restaurant.id]: (prev[restaurant.id] || 1) + 1
                              }));
                            }}
                          >
                            See more events... ({filteredEvents.length - visibleEvents.length} more)
                          </button>
                        )}
                        {hasMoreEvents && (
                          <button
                            className="see-all-events-btn"
                            onClick={() => {
                              setExpandedVenues(prev => ({
                                ...prev,
                                [restaurant.id]: { ...prev[restaurant.id], events: true, allEvents: true }
                              }));
                            }}
                          >
                            More Upcoming Events at {restaurant.fields.Name} →
                          </button>
                        )}
                        {hasUpcomingBeyondFilter && !showingUpcoming && (
                          <button
                            className="see-all-events-btn"
                            onClick={() => {
                              setExpandedVenues(prev => ({
                                ...prev,
                                [restaurant.id]: { ...prev[restaurant.id], upcomingEvents: true }
                              }));
                            }}
                          >
                            More Upcoming Events at {restaurant.fields.Name} →
                          </button>
                        )}
                        {showingUpcoming && visibleUpcoming.map(event => {
                          const scrapedImage = event.fields['Event_Image']?.[0]?.url;
                          const eventType = event.fields['Event Type'];
                          const categoryImage = getEventCategoryImage(eventType);
                          const venueThumb = restaurant.fields['Thumbnail Image']?.[0]?.url;
                          const displayImage = scrapedImage || categoryImage;
                          return (
                            <div key={event.id} id={`item-${event.id}`} className="event event-with-image">
                              <img
                                src={displayImage}
                                alt={event.fields['Event Name']}
                                className="event-image"
                                onError={(e) => {
                                  const failedSrcs = e.target.dataset.failedSrcs ? e.target.dataset.failedSrcs.split(',') : [];
                                  const currentSrc = e.target.src;
                                  failedSrcs.push(currentSrc);
                                  e.target.dataset.failedSrcs = failedSrcs.join(',');
                                  if (venueThumb && !failedSrcs.includes(venueThumb)) {
                                    e.target.src = venueThumb;
                                  } else {
                                    e.target.onerror = null;
                                    e.target.style.display = 'none';
                                    e.target.parentElement.classList.remove('event-with-image');
                                  }
                                }}
                              />
                              <div className="event-content">
                                <div className="event-header">
                                  <strong>{formatTextCase(event.fields['Event Name'])}</strong>
                                  {event.fields['Event Start'] && parseEventDate(event.fields['Event Start']) && (
                                    <button
                                      className="add-to-calendar-btn"
                                      onClick={() => openCalendarModal(event, restaurant.fields.Name, restaurant.fields.Address)}
                                      title="Add to Calendar"
                                    >
                                      + Calendar
                                    </button>
                                  )}
                                </div>
                                <p>{renderMarkdown(event.fields.Description)}</p>
                                <div className="event-details">
                                  <span className="event-type">{event.fields['Event Type']}</span>
                                  <span className="event-date">
                                    <img src="/images/date_icon.png" alt="Date" className="date-icon" onError={(e) => e.target.style.display = 'none'} />
                                    {formatEventDate(event)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {showingUpcoming && hasMoreUpcomingPaginated && (
                          <button
                            className="see-all-events-btn"
                            onClick={() => {
                              setEventPages(prev => ({
                                ...prev,
                                [`${restaurant.id}_upcoming`]: (prev[`${restaurant.id}_upcoming`] || 1) + 1
                              }));
                            }}
                          >
                            See more events... ({upcomingEvents.length - visibleUpcoming.length} more)
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
              })());
            });

            // Show empty state if no results
            if (elements.length === 0 && activeFilterCount > 0) {
              return (
                <div className="no-results-message">
                  <p className="no-results-text">
                    {filters.happeningNow
                      ? "Nothing is happening right now. Try adjusting your filters!"
                      : "No venues match your current filters. Try adjusting your search!"}
                  </p>
                  <button
                    className="no-results-clear-btn"
                    onClick={() => {
                      setFilters({ when: [], what: [], where: market.defaultCities, happeningNow: false });
                      setExpandedVenues({});
                    }}
                  >
                    Clear All Filters
                  </button>
                </div>
              );
            }

            return elements;
          })()}
        </section>
      </main>

      {/* Calendar Modal */}
      {calendarModal && (
        <div className="modal-overlay" onClick={closeCalendarModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add to Calendar</h3>
              <button className="modal-close" onClick={closeCalendarModal}>&times;</button>
            </div>
            <div className="modal-body">
              <p className="modal-event-name">{calendarModal.event.fields['Event Name']}</p>
              <p className="modal-event-details">
                {formatEventDate(calendarModal.event.fields['Event Start'])} at {calendarModal.restaurantName}
              </p>
              <div className="calendar-options">
                <a
                  href={calendarModal.links.google}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="calendar-option google"
                  onClick={closeCalendarModal}
                >
                  <span className="calendar-icon">📅</span>
                  <span>Google Calendar</span>
                </a>
                <a
                  href={calendarModal.links.outlook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="calendar-option outlook"
                  onClick={closeCalendarModal}
                >
                  <span className="calendar-icon">📧</span>
                  <span>Outlook Calendar</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Clear Filters Button - only when filter bar scrolls off screen */}
      {showFloatingClear && (
        <button
          className="floating-clear-filters-btn"
          onClick={() => {
            setFilters({ when: [], what: [], where: market.defaultCities, happeningNow: false });
            setExpandedVenues({});
          }}
        >
          ✕ Clear Filters
        </button>
      )}

      {/* Footer */}
      <footer className="app-footer">
        <button onClick={handleLogout} className="logout-button-footer">
          Logout
        </button>
        {version && (
          <span className="version-indicator-footer" title={`Build: ${version.buildTime}`}>
            v{version.commit}
          </span>
        )}
      </footer>
    </div>
  );
}

export default HomePage;
